import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  LinearProgress,
  IconButton,
  Alert,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  CircularProgress,
  Pagination,
  Collapse,
  Paper,
  alpha,
  useTheme,
  Tab,
  Tabs,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import FolderIcon from "@mui/icons-material/Folder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VerifiedIcon from "@mui/icons-material/Verified";
import TorrentIcon from "@mui/icons-material/CloudDownload";
import LinkIcon from "@mui/icons-material/Link";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const ENGINES = [
  {
    id: "piratebay",
    label: "The Pirate Bay",
    short: "TPB",
    color: "#1a9e3f",
    description:
      "Largest torrent index. Has categories & instant magnet links.",
  },
  {
    id: "torlock",
    label: "Torlock",
    short: "TL",
    color: "#e65c00",
    description: "Verified torrents only. Strong for movies & TV.",
  },
  {
    id: "torrentz2",
    label: "Torrentz2",
    short: "TZ2",
    color: "#1565c0",
    description: "Meta-search engine. Wide coverage across categories.",
  },
];

const TPB_CATEGORIES = [
  { label: "All", value: 0 },
  { label: "Movies", value: 201 },
  { label: "HD Movies", value: 207 },
  { label: "4K Movies", value: 211 },
  { label: "TV Shows", value: 205 },
  { label: "HD TV", value: 208 },
  { label: "4K TV", value: 212 },
  { label: "Music", value: 101 },
  { label: "Games PC", value: 401 },
  { label: "Software", value: 301 },
  { label: "eBooks", value: 601 },
  { label: "Anime", value: 205 },
];

const TORLOCK_CATEGORIES = [
  { label: "All", value: "all" },
  { label: "Movies", value: "movie" },
  { label: "TV", value: "television" },
  { label: "Games", value: "game" },
  { label: "Music", value: "music" },
  { label: "Software", value: "software" },
  { label: "Anime", value: "anime" },
  { label: "eBooks", value: "ebook" },
];

const statusColor = (s) =>
  ({
    completed: "success",
    downloading: "info",
    pending: "warning",
    failed: "error",
  })[s] || "default";

const formatSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return "—";
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / 1024 ** 2;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

// ─── Engine Selector ─────────────────────────────────────────────
const EngineSelector = ({ value, onChange, lang }) => {
  const theme = useTheme();
  return (
    <Stack spacing={1.5}>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ letterSpacing: "0.08em", color: "text.secondary" }}
      >
        {lang.selectEngine || "SELECT ENGINE"}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {ENGINES.map((e) => {
          const selected = value === e.id;
          return (
            <Box
              key={e.id}
              onClick={() => onChange(e.id)}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                cursor: "pointer",
                border: "1.5px solid",
                borderColor: selected
                  ? e.color
                  : alpha(theme.palette.divider, 0.8),
                bgcolor: selected ? alpha(e.color, 0.1) : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: e.color,
                  bgcolor: alpha(e.color, 0.06),
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: selected ? e.color : "text.disabled",
                    transition: "background 0.2s",
                  }}
                />
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: selected ? e.color : "text.primary" }}
                >
                  {e.short}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {e.label}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
      >
        {ENGINES.find((e) => e.id === value)?.description}
      </Typography>
    </Stack>
  );
};

// ─── Result Row ───────────────────────────────────────────────────
const ResultRow = ({ result, engine, onUse, onDetails, lang }) => {
  const theme = useTheme();
  const hasMagnet = Boolean(result.magnetLink);
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2.5,
        borderColor: alpha(theme.palette.divider, 0.6),
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          transform: "translateX(2px)",
        },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={1.5}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
            {result.verified && (
              <Tooltip title={lang.verifiedTorrent || "Verified Torrent"}>
                <VerifiedIcon sx={{ fontSize: 14, color: "success.main" }} />
              </Tooltip>
            )}
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ color: "text.primary" }}
            >
              {result.name}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
          >
            {result.size && (
              <Typography variant="caption" color="text.secondary">
                📦 {result.size}
              </Typography>
            )}
            <Chip
              label={`▲ ${result.seeders ?? "—"}`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
            />
            <Chip
              label={`▼ ${result.leechers ?? "—"}`}
              size="small"
              color="error"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem" }}
            />
            {result.category && (
              <Chip
                label={result.category}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
            )}
            {result.added && (
              <Typography variant="caption" color="text.secondary">
                🗓 {result.added}
              </Typography>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.75} flexShrink={0}>
          {hasMagnet ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => onUse(result.magnetLink)}
              sx={{ borderRadius: 2, fontWeight: 700, whiteSpace: "nowrap" }}
            >
              {lang.add || "Add"}
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              onClick={() => onDetails(result)}
              sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
            >
              {lang.details || "Details"}
            </Button>
          )}
          <Tooltip title={lang.viewDetails || "View details & files"}>
            <IconButton
              size="small"
              onClick={() => onDetails(result)}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};

// ─── Detail Dialog ────────────────────────────────────────────────
const DetailDialog = ({
  open,
  onClose,
  engine,
  result,
  onAddMagnet,
  hitAxios,
  lang,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filesOpen, setFilesOpen] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (!open || !result) return;
    fetchDetail();
  }, [open, result]);

  const fetchDetail = async () => {
    setLoading(true);
    setError(null);
    setDetail(null);
    try {
      const res = await hitAxios({
        path: "/api/torrent/details",
        post: true,
        obj: {
          engine,
          id: result.id,
          slug: result.slug || null,
          url: result.url || null,
        },
        showSnackbar: false,
        showLoading: false,
      });
      if (res?.data?.success) setDetail(res.data.detail);
      else
        setError(
          res?.data?.msg ||
            lang.failedToLoadDetails ||
            "Failed to load details.",
        );
    } catch (e) {
      setError(lang.networkError || "Network error.");
    } finally {
      setLoading(false);
    }
  };

  const engineMeta = ENGINES.find((e) => e.id === engine);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight={700} sx={{ flex: 1, pr: 2 }}>
            {result?.name || lang.torrentDetails || "Torrent Details"}
          </Typography>
          <Chip
            label={engineMeta?.short}
            size="small"
            sx={{
              bgcolor: engineMeta?.color,
              color: "#fff",
              fontWeight: 700,
            }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" py={5}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" mt={1.5}>
              {lang.fetchingDetails || "Fetching torrent details..."}
            </Typography>
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {detail && !loading && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {detail.size && (
                <Chip
                  icon={<span>📦</span>}
                  label={detail.size}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                label={`▲ ${detail.seeders} ${lang.seeders || "seeders"}`}
                size="small"
                color="success"
                variant="outlined"
              />
              <Chip
                label={`▼ ${detail.leechers} ${lang.leechers || "leechers"}`}
                size="small"
                color="error"
                variant="outlined"
              />
              {detail.category && (
                <Chip label={detail.category} size="small" variant="outlined" />
              )}
              {detail.added && (
                <Chip
                  label={`${lang.added || "Added"}: ${detail.added}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {detail.uploader && (
                <Chip
                  label={`${lang.by || "By"}: ${detail.uploader}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>

            {detail.infoHash && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  {lang.infoHash || "INFO HASH"}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    mt: 0.5,
                  }}
                >
                  {detail.infoHash}
                </Typography>
              </Paper>
            )}

            {detail.description && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  {lang.description || "DESCRIPTION"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.75,
                    maxHeight: 120,
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    fontSize: "0.8rem",
                    color: "text.secondary",
                  }}
                >
                  {detail.description}
                </Typography>
              </Box>
            )}

            {detail.files && detail.files.length > 0 && (
              <Box>
                <Button
                  size="small"
                  startIcon={<FolderIcon />}
                  endIcon={filesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setFilesOpen((v) => !v)}
                  sx={{ mb: 0.5, pl: 0, fontWeight: 600 }}
                >
                  {detail.files.length} {lang.files || "Files"}
                </Button>
                <Collapse in={filesOpen}>
                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      borderRadius: 2,
                      p: 1,
                    }}
                  >
                    {detail.files.map((f, i) => (
                      <Stack
                        key={i}
                        direction="row"
                        justifyContent="space-between"
                        py={0.4}
                        sx={{
                          borderBottom:
                            i < detail.files.length - 1 ? "1px solid" : "none",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          📄 {f.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1, flexShrink: 0 }}
                        >
                          {f.sizeFormatted || f.size || "—"}
                        </Typography>
                      </Stack>
                    ))}
                  </Paper>
                </Collapse>
              </Box>
            )}

            {detail.imdbId && (
              <Button
                size="small"
                variant="outlined"
                href={`https://www.imdb.com/title/${detail.imdbId}`}
                target="_blank"
                startIcon={<OpenInNewIcon />}
                sx={{ alignSelf: "flex-start", borderRadius: 2 }}
              >
                {lang.viewOnImdb || "View on IMDB"}
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
        {detail?.torrentFileUrl && (
          <Button
            variant="outlined"
            size="small"
            href={detail.torrentFileUrl}
            target="_blank"
            startIcon={<DownloadIcon />}
            sx={{ borderRadius: 2 }}
          >
            {lang.torrentFile || ".torrent File"}
          </Button>
        )}
        {detail?.magnetLink && (
          <Button
            variant="outlined"
            size="small"
            href={detail.magnetLink}
            startIcon={<OpenInNewIcon />}
            sx={{ borderRadius: 2 }}
          >
            {lang.openInClient || "Open in Client"}
          </Button>
        )}
        {detail?.magnetLink && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              onAddMagnet(detail.magnetLink);
              onClose();
            }}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {lang.addToDownloads || "Add to Downloads"}
          </Button>
        )}
        {!detail?.magnetLink && !loading && (
          <Alert severity="info" sx={{ flex: 1, py: 0.5, borderRadius: 2 }}>
            {lang.noMagnetAvailable ||
              "No magnet link available for this torrent."}
          </Alert>
        )}
        <Button
          onClick={onClose}
          color="inherit"
          size="small"
          sx={{ borderRadius: 2 }}
        >
          {lang.close || "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ───────────────────────────────────────────────
const DownloadTorrent = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();

  const [tabValue, setTabValue] = useState(0);
  const [magnet, setMagnet] = useState("");
  const [engine, setEngine] = useState("piratebay");
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchMeta, setSearchMeta] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailResult, setDetailResult] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const pollRef = useRef(null);

  useEffect(() => {
    loadDownloads();
    pollRef.current = setInterval(() => loadDownloads(true), 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    setCategory(engine === "piratebay" ? 0 : "all");
    setSearchResults([]);
    setSearchMeta(null);
    setCurrentPage(1);
  }, [engine]);

  const loadDownloads = async (silent = false) => {
    const res = await hitAxios({
      path: "/api/torrent/my-downloads",
      showSnackbar: false,
      showLoading: !silent,
    });
    if (res?.data?.success) setDownloads(res.data.data);
  };

  const addMagnetToQueue = async (magnetLink) => {
    if (!magnetLink?.startsWith("magnet:")) return;
    const res = await hitAxios({
      path: "/api/torrent/add",
      post: true,
      obj: { magnet_link: magnetLink },
    });
    if (res?.data?.success) {
      setMagnet("");
      setTabValue(2);
      loadDownloads();
    }
  };

  const deleteDownload = async (uid) => {
    await hitAxios({ path: "/api/torrent/delete", post: true, obj: { uid } });
    loadDownloads();
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.startsWith("magnet:")) setMagnet(text);
    } catch (e) {}
  };

  const doSearch = async (page = 1) => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchError(null);
    setCurrentPage(page);
    const res = await hitAxios({
      path: "/api/torrent/search",
      post: true,
      obj: { query: searchQuery.trim(), engine, page, category },
      showSnackbar: false,
      showLoading: false,
    });
    setSearching(false);
    if (res?.data?.success) {
      setSearchResults(res.data.results || []);
      setSearchMeta({
        total: res.data.total,
        totalPages: res.data.totalPages,
        page: res.data.page,
      });
    } else {
      setSearchError(res?.data?.msg || lang.searchFailed || "Search failed.");
      setSearchResults([]);
    }
  };

  const handlePageChange = (_, page) => doSearch(page);
  const openDetail = (result) => {
    setDetailResult(result);
    setDetailOpen(true);
  };

  const activeDownloads = downloads.filter((d) =>
    ["pending", "downloading"].includes(d.status),
  );
  const currentEngine = ENGINES.find((e) => e.id === engine);
  const categories =
    engine === "piratebay"
      ? TPB_CATEGORIES
      : engine === "torlock"
        ? TORLOCK_CATEGORIES
        : null;

  const TAB_ITEMS = [
    {
      icon: <SearchIcon sx={{ fontSize: 18 }} />,
      label: lang.search || "Search",
    },
    {
      icon: <LinkIcon sx={{ fontSize: 18 }} />,
      label: lang.addMagnet || "Add Magnet",
    },
    {
      icon: <TorrentIcon sx={{ fontSize: 18 }} />,
      label: lang.myDownloads || "My Downloads",
      badge: activeDownloads.length,
    },
  ];

  return (
    <Box>
      {/* ── Header ── */}
      <PageHeader
        icon={TorrentIcon}
        title={lang.torrentManager || "Torrent Manager"}
        subtitle={
          lang.torrentManagerSubtitle ||
          "Search, download and manage your torrents"
        }
      />

      {/* ── Custom Tab Bar ── */}
      <Paper
        variant="outlined"
        sx={{
          display: "inline-flex",
          borderRadius: 2.5,
          p: 0.5,
          mb: 3,
          gap: 0.5,
          borderColor: alpha(theme.palette.divider, 0.6),
        }}
      >
        {TAB_ITEMS.map((tab, idx) => (
          <Box
            key={idx}
            onClick={() => setTabValue(idx)}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              transition: "all 0.2s ease",
              bgcolor:
                tabValue === idx ? theme.palette.primary.main : "transparent",
              color: tabValue === idx ? "#fff" : "text.secondary",
              "&:hover": {
                bgcolor:
                  tabValue === idx
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.08),
                color: tabValue === idx ? "#fff" : "text.primary",
              },
            }}
          >
            {tab.icon}
            <Typography variant="body2" fontWeight={600}>
              {tab.label}
            </Typography>
            {tab.badge > 0 && (
              <Chip
                label={tab.badge}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  bgcolor:
                    tabValue === idx
                      ? alpha("#fff", 0.25)
                      : theme.palette.info.main,
                  color: "#fff",
                }}
              />
            )}
          </Box>
        ))}
      </Paper>

      {/* ── TAB 0: SEARCH ── */}
      {tabValue === 0 && (
        <Stack spacing={2.5}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: alpha(theme.palette.divider, 0.6),
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <EngineSelector
                  value={engine}
                  onChange={setEngine}
                  lang={lang}
                />
                <Divider />

                {categories && (
                  <Stack spacing={1}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ letterSpacing: "0.08em", color: "text.secondary" }}
                    >
                      {lang.category || "CATEGORY"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {categories.map((c) => (
                        <Chip
                          key={c.value}
                          label={c.label}
                          size="small"
                          onClick={() => setCategory(c.value)}
                          color={category === c.value ? "primary" : "default"}
                          variant={category === c.value ? "filled" : "outlined"}
                          sx={{
                            cursor: "pointer",
                            fontWeight: category === c.value ? 700 : 400,
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`${lang.searchOn || "Search on"} ${currentEngine?.label}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch(1)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ color: "text.disabled", fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    startIcon={
                      searching ? (
                        <CircularProgress size={15} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    onClick={() => doSearch(1)}
                    disabled={searching || !searchQuery.trim()}
                    sx={{
                      minWidth: 130,
                      borderRadius: 2,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {searching
                      ? lang.searching || "Searching..."
                      : lang.search || "Search"}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {searchError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {searchError}
            </Alert>
          )}

          {searchMeta && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              px={0.5}
            >
              <Typography variant="body2" color="text.secondary">
                {searchMeta.total > 0
                  ? `${lang.found || "Found"} `
                  : lang.noResultsFound || "No results found"}
                {searchMeta.total > 0 && (
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={700}
                    color="text.primary"
                  >
                    {searchMeta.total.toLocaleString()}
                  </Typography>
                )}
                {searchMeta.total > 0 && ` ${lang.results || "results"}`}
              </Typography>
              <Chip
                label={currentEngine?.short}
                size="small"
                sx={{
                  bgcolor: currentEngine?.color,
                  color: "#fff",
                  fontWeight: 700,
                }}
              />
            </Stack>
          )}

          {searchResults.length > 0 && (
            <Stack spacing={1}>
              {searchResults.map((result) => (
                <ResultRow
                  key={`${result.id}-${result.name}`}
                  result={result}
                  engine={engine}
                  onUse={addMagnetToQueue}
                  onDetails={openDetail}
                  lang={lang}
                />
              ))}
            </Stack>
          )}

          {searchMeta?.totalPages > 1 && (
            <Stack alignItems="center" mt={1}>
              <Pagination
                count={searchMeta.totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Stack>
          )}
        </Stack>
      )}

      {/* ── TAB 1: ADD MAGNET ── */}
      {tabValue === 1 && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderColor: alpha(theme.palette.divider, 0.6),
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={0.5} mb={3}>
              <Typography variant="h6" fontWeight={700}>
                {lang.pasteMagnetLink || "Paste Magnet Link"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lang.pasteMagnetDesc ||
                  "Paste a magnet link to add it directly to your download queue."}
              </Typography>
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                borderColor: magnet.startsWith("magnet:")
                  ? theme.palette.success.main
                  : alpha(theme.palette.divider, 0.8),
                bgcolor: magnet.startsWith("magnet:")
                  ? alpha(theme.palette.success.main, 0.04)
                  : "transparent",
                transition: "all 0.2s",
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="magnet:?xt=urn:btih:..."
                  value={magnet}
                  onChange={(e) => setMagnet(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && addMagnetToQueue(magnet)
                  }
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Tooltip
                          title={
                            lang.pasteFromClipboard || "Paste from clipboard"
                          }
                        >
                          <IconButton size="small" onClick={pasteFromClipboard}>
                            <ContentPasteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => addMagnetToQueue(magnet)}
                  disabled={!magnet.startsWith("magnet:")}
                  sx={{
                    minWidth: 150,
                    borderRadius: 2,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {lang.addDownload || "Add Download"}
                </Button>
              </Stack>
            </Paper>

            {magnet && !magnet.startsWith("magnet:") && (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                {lang.onlyMagnetSupported ||
                  "Only magnet links are supported (must start with magnet:)"}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── TAB 2: MY DOWNLOADS ── */}
      {tabValue === 2 && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderColor: alpha(theme.palette.divider, 0.6),
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2.5}
            >
              <Stack spacing={0.25}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6" fontWeight={700}>
                    {lang.myDownloads || "My Downloads"}
                  </Typography>
                  {activeDownloads.length > 0 && (
                    <Chip
                      label={`${activeDownloads.length} ${lang.active || "active"}`}
                      size="small"
                      color="info"
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {downloads.length} {lang.totalItems || "total items"}
                </Typography>
              </Stack>
              <Tooltip title={lang.refresh || "Refresh"}>
                <IconButton
                  size="small"
                  onClick={() => loadDownloads()}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {downloads.length === 0 ? (
              <Box
                textAlign="center"
                py={6}
                sx={{
                  border: "2px dashed",
                  borderColor: alpha(theme.palette.divider, 0.6),
                  borderRadius: 3,
                }}
              >
                <TorrentIcon
                  sx={{
                    fontSize: 48,
                    color: alpha(theme.palette.primary.main, 0.2),
                    mb: 1.5,
                  }}
                />
                <Typography
                  color="text.secondary"
                  variant="body2"
                  fontWeight={500}
                >
                  {lang.noDownloadsYetSearch ||
                    "No downloads yet. Search for torrents and add them!"}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {downloads.map((dl) => (
                  <Paper
                    key={dl.uid}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      borderColor:
                        dl.status === "downloading"
                          ? alpha(theme.palette.info.main, 0.4)
                          : dl.status === "completed"
                            ? alpha(theme.palette.success.main, 0.3)
                            : dl.status === "failed"
                              ? alpha(theme.palette.error.main, 0.3)
                              : alpha(theme.palette.divider, 0.6),
                      transition: "all 0.2s",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box sx={{ flex: 1, mr: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {dl.torrent_name ||
                            lang.fetchingTorrentInfo ||
                            "Fetching torrent info..."}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          mt={0.75}
                          flexWrap="wrap"
                          useFlexGap
                          alignItems="center"
                        >
                          <Chip
                            label={dl.status}
                            size="small"
                            color={statusColor(dl.status)}
                            sx={{
                              fontWeight: 700,
                              height: 20,
                              fontSize: "0.68rem",
                            }}
                          />
                          {dl.total_size > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatSize(dl.downloaded_size)} /{" "}
                              {formatSize(dl.total_size)}
                            </Typography>
                          )}
                          {dl.download_speed > 0 && (
                            <Typography
                              variant="caption"
                              color="info.main"
                              fontWeight={600}
                            >
                              ↓ {formatSize(dl.download_speed)}/s
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.disabled">
                            {new Date(dl.createdAt).toLocaleString()}
                          </Typography>
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        {dl.status === "failed" && (
                          <Tooltip title={lang.retry || "Retry"}>
                            <IconButton
                              size="small"
                              color="warning"
                              sx={{
                                border: "1px solid",
                                borderColor: "warning.main",
                                borderRadius: 1.5,
                              }}
                              onClick={async () => {
                                await hitAxios({
                                  path: "/api/torrent/retry",
                                  post: true,
                                  obj: { uid: dl.uid },
                                });
                                loadDownloads();
                              }}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={lang.delete || "Delete"}>
                          <IconButton
                            size="small"
                            color="error"
                            sx={{
                              border: "1px solid",
                              borderColor: "error.main",
                              borderRadius: 1.5,
                            }}
                            onClick={() => deleteDownload(dl.uid)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    {dl.status === "downloading" && (
                      <Box mt={1.5}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          mb={0.5}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {lang.progress || "Progress"}
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color="info.main"
                          >
                            {parseFloat(dl.progress || 0).toFixed(1)}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={parseFloat(dl.progress || 0)}
                          sx={{
                            borderRadius: 2,
                            height: 6,
                            bgcolor: alpha(theme.palette.info.main, 0.12),
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 2,
                              background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.primary.main})`,
                            },
                          }}
                        />
                      </Box>
                    )}

                    {dl.status === "completed" && dl.files && (
                      <Box
                        mt={1.5}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.success.main, 0.05),
                          border: "1px solid",
                          borderColor: alpha(theme.palette.success.main, 0.15),
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          {lang.files || "FILES"}
                        </Typography>
                        {(typeof dl.files === "string"
                          ? JSON.parse(dl.files)
                          : dl.files
                        )
                          ?.slice(0, 3)
                          .map((f, i) => (
                            <Typography
                              key={i}
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              noWrap
                              sx={{ mt: 0.3 }}
                            >
                              📄 {f.name} ({formatSize(f.length)})
                            </Typography>
                          ))}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      <DetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        engine={engine}
        result={detailResult}
        onAddMagnet={addMagnetToQueue}
        hitAxios={hitAxios}
        lang={lang}
      />
    </Box>
  );
};

export default DownloadTorrent;
